// LMS reference tables, 0–15 months. See growth-spec §4–§5.
// Ages stored as decimal years (days / 365.25 for NL4 month rounding).

import type { LMSRow, Metric, ReferenceId, Sex } from './lms.ts';
import { PRETERM_WGT } from './preterm.ts';

// ─── WHO 2006 — weight-for-age ──────────────────────────────
const WHO_WEIGHT_BOYS: LMSRow[] = [
  { age_yr: 0 / 12, L: 0.3487, M: 3.3464, S: 0.14602 },
  { age_yr: 1 / 12, L: 0.2303, M: 4.4525, S: 0.13413 },
  { age_yr: 2 / 12, L: 0.1969, M: 5.5714, S: 0.12382 },
  { age_yr: 3 / 12, L: 0.1740, M: 6.3690, S: 0.11732 },
  { age_yr: 4 / 12, L: 0.1551, M: 7.0069, S: 0.11313 },
  { age_yr: 5 / 12, L: 0.1396, M: 7.5077, S: 0.11081 },
  { age_yr: 6 / 12, L: 0.1256, M: 7.9389, S: 0.10957 },
  { age_yr: 7 / 12, L: 0.1134, M: 8.2963, S: 0.10902 },
  { age_yr: 8 / 12, L: 0.1019, M: 8.6200, S: 0.10882 },
  { age_yr: 9 / 12, L: 0.0917, M: 8.9019, S: 0.10881 },
  { age_yr: 10 / 12, L: 0.0821, M: 9.1618, S: 0.10890 },
  { age_yr: 11 / 12, L: 0.0729, M: 9.4136, S: 0.10906 },
  { age_yr: 12 / 12, L: 0.0645, M: 9.6460, S: 0.10925 },
  { age_yr: 13 / 12, L: 0.0563, M: 9.8772, S: 0.10949 },
  { age_yr: 14 / 12, L: 0.0487, M: 10.0944, S: 0.10976 },
  { age_yr: 15 / 12, L: 0.0412, M: 10.3139, S: 0.11008 },
];

const WHO_WEIGHT_GIRLS: LMSRow[] = [
  { age_yr: 0 / 12, L: 0.3809, M: 3.2322, S: 0.14171 },
  { age_yr: 1 / 12, L: 0.1727, M: 4.1716, S: 0.13738 },
  { age_yr: 2 / 12, L: 0.0959, M: 5.1315, S: 0.12998 },
  { age_yr: 3 / 12, L: 0.0407, M: 5.8393, S: 0.12622 },
  { age_yr: 4 / 12, L: -0.0053, M: 6.4280, S: 0.12401 },
  { age_yr: 5 / 12, L: -0.0428, M: 6.8959, S: 0.12274 },
  { age_yr: 6 / 12, L: -0.0759, M: 7.3016, S: 0.12204 },
  { age_yr: 7 / 12, L: -0.1039, M: 7.6416, S: 0.12178 },
  { age_yr: 8 / 12, L: -0.1292, M: 7.9534, S: 0.12181 },
  { age_yr: 9 / 12, L: -0.1507, M: 8.2259, S: 0.12199 },
  { age_yr: 10 / 12, L: -0.1698, M: 8.4769, S: 0.12222 },
  { age_yr: 11 / 12, L: -0.1873, M: 8.7207, S: 0.12247 },
  { age_yr: 12 / 12, L: -0.2022, M: 8.9462, S: 0.12267 },
  { age_yr: 13 / 12, L: -0.2160, M: 9.1722, S: 0.12283 },
  { age_yr: 14 / 12, L: -0.2277, M: 9.3861, S: 0.12294 },
  { age_yr: 15 / 12, L: -0.2385, M: 9.6038, S: 0.12299 },
];

// ─── WHO 2006 — length-for-age ──────────────────────────────
const WHO_LENGTH_BOYS: LMSRow[] = [
  { age_yr: 0 / 12, L: 1, M: 49.8842, S: 0.03795 },
  { age_yr: 1 / 12, L: 1, M: 54.6645, S: 0.03559 },
  { age_yr: 2 / 12, L: 1, M: 58.4384, S: 0.03423 },
  { age_yr: 3 / 12, L: 1, M: 61.4013, S: 0.03329 },
  { age_yr: 4 / 12, L: 1, M: 63.9041, S: 0.03257 },
  { age_yr: 5 / 12, L: 1, M: 65.8912, S: 0.03204 },
  { age_yr: 6 / 12, L: 1, M: 67.6435, S: 0.03165 },
  { age_yr: 7 / 12, L: 1, M: 69.1615, S: 0.03139 },
  { age_yr: 8 / 12, L: 1, M: 70.6224, S: 0.03124 },
  { age_yr: 9 / 12, L: 1, M: 71.9714, S: 0.03117 },
  { age_yr: 10 / 12, L: 1, M: 73.2653, S: 0.03118 },
  { age_yr: 11 / 12, L: 1, M: 74.5464, S: 0.03125 },
  { age_yr: 12 / 12, L: 1, M: 75.7391, S: 0.03137 },
  { age_yr: 13 / 12, L: 1, M: 76.9304, S: 0.03154 },
  { age_yr: 14 / 12, L: 1, M: 78.0451, S: 0.03174 },
  { age_yr: 15 / 12, L: 1, M: 79.1613, S: 0.03197 },
];

const WHO_LENGTH_GIRLS: LMSRow[] = [
  { age_yr: 0 / 12, L: 1, M: 49.1477, S: 0.03790 },
  { age_yr: 1 / 12, L: 1, M: 53.6326, S: 0.03641 },
  { age_yr: 2 / 12, L: 1, M: 57.0796, S: 0.03568 },
  { age_yr: 3 / 12, L: 1, M: 59.7773, S: 0.03520 },
  { age_yr: 4 / 12, L: 1, M: 62.1071, S: 0.03486 },
  { age_yr: 5 / 12, L: 1, M: 64.0190, S: 0.03463 },
  { age_yr: 6 / 12, L: 1, M: 65.7510, S: 0.03448 },
  { age_yr: 7 / 12, L: 1, M: 67.2842, S: 0.03441 },
  { age_yr: 8 / 12, L: 1, M: 68.7732, S: 0.03440 },
  { age_yr: 9 / 12, L: 1, M: 70.1463, S: 0.03444 },
  { age_yr: 10 / 12, L: 1, M: 71.4656, S: 0.03452 },
  { age_yr: 11 / 12, L: 1, M: 72.7788, S: 0.03464 },
  { age_yr: 12 / 12, L: 1, M: 74.0049, S: 0.03479 },
  { age_yr: 13 / 12, L: 1, M: 75.2297, S: 0.03496 },
  { age_yr: 14 / 12, L: 1, M: 76.3770, S: 0.03514 },
  { age_yr: 15 / 12, L: 1, M: 77.5258, S: 0.03534 },
];

// ─── NL4 (Fourth Dutch Growth Study, 1997) — weight-for-age ─
const NL4_WEIGHT_BOYS: LMSRow[] = [
  { age_yr: 0.0000, L: 0.629, M: 3.55, S: 0.115 },
  { age_yr: 0.0767, L: 0.580, M: 4.33, S: 0.115 },
  { age_yr: 0.1725, L: 0.523, M: 5.30, S: 0.114 },
  { age_yr: 0.2683, L: 0.468, M: 6.19, S: 0.114 },
  { age_yr: 0.3450, L: 0.428, M: 6.82, S: 0.113 },
  { age_yr: 0.4216, L: 0.391, M: 7.39, S: 0.113 },
  { age_yr: 0.4983, L: 0.357, M: 7.89, S: 0.113 },
  { age_yr: 0.6133, L: 0.311, M: 8.55, S: 0.112 },
  { age_yr: 0.6899, L: 0.285, M: 8.95, S: 0.112 },
  { age_yr: 0.7666, L: 0.260, M: 9.30, S: 0.112 },
  { age_yr: 0.8433, L: 0.237, M: 9.63, S: 0.112 },
  { age_yr: 0.9199, L: 0.216, M: 9.94, S: 0.112 },
  { age_yr: 0.9966, L: 0.196, M: 10.23, S: 0.112 },
  { age_yr: 1.0732, L: 0.177, M: 10.50, S: 0.113 },
  { age_yr: 1.1499, L: 0.160, M: 10.75, S: 0.113 },
  { age_yr: 1.2266, L: 0.143, M: 11.00, S: 0.113 },
];

const NL4_WEIGHT_GIRLS: LMSRow[] = [
  { age_yr: 0.0000, L: 0.369, M: 3.34, S: 0.115 },
  { age_yr: 0.0767, L: 0.328, M: 4.03, S: 0.115 },
  { age_yr: 0.1725, L: 0.280, M: 4.90, S: 0.113 },
  { age_yr: 0.2683, L: 0.233, M: 5.72, S: 0.112 },
  { age_yr: 0.3450, L: 0.198, M: 6.32, S: 0.112 },
  { age_yr: 0.4216, L: 0.166, M: 6.85, S: 0.111 },
  { age_yr: 0.4983, L: 0.137, M: 7.33, S: 0.110 },
  { age_yr: 0.6133, L: 0.099, M: 7.95, S: 0.110 },
  { age_yr: 0.6899, L: 0.076, M: 8.32, S: 0.110 },
  { age_yr: 0.7666, L: 0.056, M: 8.66, S: 0.110 },
  { age_yr: 0.8433, L: 0.036, M: 8.97, S: 0.109 },
  { age_yr: 0.9199, L: 0.018, M: 9.27, S: 0.110 },
  { age_yr: 0.9966, L: 0.002, M: 9.55, S: 0.110 },
  { age_yr: 1.0732, L: -0.014, M: 9.81, S: 0.110 },
  { age_yr: 1.1499, L: -0.029, M: 10.06, S: 0.110 },
  { age_yr: 1.2266, L: -0.043, M: 10.30, S: 0.110 },
];

// ─── NL4 — length-for-age ────────────────────────────────────
const NL4_LENGTH_BOYS: LMSRow[] = [
  { age_yr: 0.0000, L: 1, M: 51.32, S: 0.0405 },
  { age_yr: 0.0767, L: 1, M: 54.46, S: 0.0400 },
  { age_yr: 0.1725, L: 1, M: 58.36, S: 0.0391 },
  { age_yr: 0.2683, L: 1, M: 61.82, S: 0.0382 },
  { age_yr: 0.3450, L: 1, M: 64.16, S: 0.0376 },
  { age_yr: 0.4216, L: 1, M: 66.18, S: 0.0370 },
  { age_yr: 0.4983, L: 1, M: 67.94, S: 0.0368 },
  { age_yr: 0.6133, L: 1, M: 70.25, S: 0.0363 },
  { age_yr: 0.6899, L: 1, M: 71.63, S: 0.0362 },
  { age_yr: 0.7666, L: 1, M: 72.93, S: 0.0361 },
  { age_yr: 0.8433, L: 1, M: 74.17, S: 0.0360 },
  { age_yr: 0.9199, L: 1, M: 75.36, S: 0.0360 },
  { age_yr: 0.9966, L: 1, M: 76.50, S: 0.0359 },
  { age_yr: 1.0732, L: 1, M: 77.60, S: 0.0358 },
  { age_yr: 1.1499, L: 1, M: 78.66, S: 0.0359 },
  { age_yr: 1.2266, L: 1, M: 79.69, S: 0.0359 },
];

const NL4_LENGTH_GIRLS: LMSRow[] = [
  { age_yr: 0.0000, L: 1, M: 50.90, S: 0.0376 },
  { age_yr: 0.0767, L: 1, M: 53.63, S: 0.0373 },
  { age_yr: 0.1725, L: 1, M: 57.02, S: 0.0367 },
  { age_yr: 0.2683, L: 1, M: 60.22, S: 0.0360 },
  { age_yr: 0.3450, L: 1, M: 62.54, S: 0.0355 },
  { age_yr: 0.4216, L: 1, M: 64.60, S: 0.0353 },
  { age_yr: 0.4983, L: 1, M: 66.41, S: 0.0349 },
  { age_yr: 0.6133, L: 1, M: 68.73, S: 0.0347 },
  { age_yr: 0.6899, L: 1, M: 70.11, S: 0.0346 },
  { age_yr: 0.7666, L: 1, M: 71.41, S: 0.0345 },
  { age_yr: 0.8433, L: 1, M: 72.66, S: 0.0344 },
  { age_yr: 0.9199, L: 1, M: 73.89, S: 0.0345 },
  { age_yr: 0.9966, L: 1, M: 75.09, S: 0.0346 },
  { age_yr: 1.0732, L: 1, M: 76.27, S: 0.0344 },
  { age_yr: 1.1499, L: 1, M: 77.42, S: 0.0347 },
  { age_yr: 1.2266, L: 1, M: 78.53, S: 0.0347 },
];

// ─── NL5 (Fifth Dutch Growth Study, 2009) — length-for-age ──
// Source: growthcharts/nlreferences (AGPL data file, scientific LMS values).
// Schönbeck 2013 — "The world's tallest nation has stopped growing taller."
// NL subpopulation only. Ages 0–~1.5y (clamp earlier).
const NL5_LENGTH_BOYS: LMSRow[] = [
  { age_yr: 0.0383, L: 1, M: 52.96, S: 0.0439 },
  { age_yr: 0.0575, L: 1, M: 53.7,  S: 0.0434 },
  { age_yr: 0.0767, L: 1, M: 54.44, S: 0.043  },
  { age_yr: 0.0958, L: 1, M: 55.17, S: 0.0425 },
  { age_yr: 0.115,  L: 1, M: 55.91, S: 0.0421 },
  { age_yr: 0.1342, L: 1, M: 56.64, S: 0.0417 },
  { age_yr: 0.1533, L: 1, M: 57.36, S: 0.0413 },
  { age_yr: 0.1725, L: 1, M: 58.08, S: 0.0409 },
  { age_yr: 0.1916, L: 1, M: 58.79, S: 0.0405 },
  { age_yr: 0.23,   L: 1, M: 60.17, S: 0.0398 },
  { age_yr: 0.2683, L: 1, M: 61.49, S: 0.0392 },
  { age_yr: 0.3066, L: 1, M: 62.75, S: 0.0387 },
  { age_yr: 0.345,  L: 1, M: 63.94, S: 0.0382 },
  { age_yr: 0.3833, L: 1, M: 65.06, S: 0.0378 },
  { age_yr: 0.4216, L: 1, M: 66.11, S: 0.0374 },
  { age_yr: 0.46,   L: 1, M: 67.08, S: 0.0371 },
  { age_yr: 0.4983, L: 1, M: 67.99, S: 0.0368 },
  { age_yr: 0.5366, L: 1, M: 68.85, S: 0.0366 },
  { age_yr: 0.6133, L: 1, M: 70.41, S: 0.0363 },
  { age_yr: 0.6899, L: 1, M: 71.83, S: 0.036  },
  { age_yr: 0.7666, L: 1, M: 73.14, S: 0.0358 },
  { age_yr: 0.8433, L: 1, M: 74.37, S: 0.0357 },
  { age_yr: 0.9199, L: 1, M: 75.52, S: 0.0356 },
  { age_yr: 0.9966, L: 1, M: 76.61, S: 0.0355 },
  { age_yr: 1.0732, L: 1, M: 77.64, S: 0.0355 },
  { age_yr: 1.1499, L: 1, M: 78.64, S: 0.0355 },
  { age_yr: 1.2266, L: 1, M: 79.6,  S: 0.0355 },
  { age_yr: 1.5,    L: 1, M: 82.83, S: 0.0356 },
];

const NL5_LENGTH_GIRLS: LMSRow[] = [
  { age_yr: 0.0192, L: 1, M: 51.55, S: 0.0449 },
  { age_yr: 0.0383, L: 1, M: 52.28, S: 0.0445 },
  { age_yr: 0.0575, L: 1, M: 53,    S: 0.0441 },
  { age_yr: 0.0767, L: 1, M: 53.71, S: 0.0437 },
  { age_yr: 0.0958, L: 1, M: 54.41, S: 0.0433 },
  { age_yr: 0.115,  L: 1, M: 55.1,  S: 0.0429 },
  { age_yr: 0.1342, L: 1, M: 55.78, S: 0.0426 },
  { age_yr: 0.1533, L: 1, M: 56.45, S: 0.0422 },
  { age_yr: 0.1725, L: 1, M: 57.12, S: 0.0419 },
  { age_yr: 0.1916, L: 1, M: 57.77, S: 0.0416 },
  { age_yr: 0.23,   L: 1, M: 59.03, S: 0.041  },
  { age_yr: 0.2683, L: 1, M: 60.24, S: 0.0405 },
  { age_yr: 0.3066, L: 1, M: 61.4,  S: 0.04   },
  { age_yr: 0.345,  L: 1, M: 62.49, S: 0.0396 },
  { age_yr: 0.3833, L: 1, M: 63.53, S: 0.0392 },
  { age_yr: 0.4216, L: 1, M: 64.52, S: 0.0389 },
  { age_yr: 0.46,   L: 1, M: 65.46, S: 0.0386 },
  { age_yr: 0.4983, L: 1, M: 66.35, S: 0.0384 },
  { age_yr: 0.5366, L: 1, M: 67.19, S: 0.0382 },
  { age_yr: 0.6133, L: 1, M: 68.75, S: 0.0378 },
  { age_yr: 0.6899, L: 1, M: 70.17, S: 0.0376 },
  { age_yr: 0.7666, L: 1, M: 71.48, S: 0.0375 },
  { age_yr: 0.8433, L: 1, M: 72.71, S: 0.0374 },
  { age_yr: 0.9199, L: 1, M: 73.87, S: 0.0373 },
  { age_yr: 0.9966, L: 1, M: 74.98, S: 0.0373 },
  { age_yr: 1.0732, L: 1, M: 76.06, S: 0.0373 },
  { age_yr: 1.1499, L: 1, M: 77.1,  S: 0.0373 },
  { age_yr: 1.2266, L: 1, M: 78.11, S: 0.0373 },
  { age_yr: 1.5,    L: 1, M: 81.48, S: 0.0375 },
];

// ─── NL5 — weight-for-age (Schönbeck 2011) ──────────────────
const NL5_WEIGHT_BOYS: LMSRow[] = [
  { age_yr: 0.0383, L: 1.4638, M: 3.9543,  S: 0.119  },
  { age_yr: 0.0575, L: 1.443,  M: 4.1581,  S: 0.1189 },
  { age_yr: 0.0767, L: 1.4221, M: 4.3617,  S: 0.1186 },
  { age_yr: 0.0958, L: 1.4013, M: 4.5639,  S: 0.1182 },
  { age_yr: 0.115,  L: 1.3805, M: 4.766,   S: 0.1177 },
  { age_yr: 0.1342, L: 1.3596, M: 4.9664,  S: 0.1177 },
  { age_yr: 0.1533, L: 1.339,  M: 5.163,   S: 0.1172 },
  { age_yr: 0.1725, L: 1.3182, M: 5.3572,  S: 0.1168 },
  { age_yr: 0.1916, L: 1.2976, M: 5.5461,  S: 0.1165 },
  { age_yr: 0.23,   L: 1.2566, M: 5.9123,  S: 0.1159 },
  { age_yr: 0.2683, L: 1.2161, M: 6.2584,  S: 0.1152 },
  { age_yr: 0.3066, L: 1.1762, M: 6.5844,  S: 0.1146 },
  { age_yr: 0.345,  L: 1.1369, M: 6.8907,  S: 0.1144 },
  { age_yr: 0.3833, L: 1.0985, M: 7.1755,  S: 0.1141 },
  { age_yr: 0.4216, L: 1.061,  M: 7.4405,  S: 0.1131 },
  { age_yr: 0.46,   L: 1.0243, M: 7.6879,  S: 0.113  },
  { age_yr: 0.4983, L: 0.9886, M: 7.9179,  S: 0.1124 },
  { age_yr: 0.5366, L: 0.954,  M: 8.1323,  S: 0.1122 },
  { age_yr: 0.6133, L: 0.8877, M: 8.5236,  S: 0.1108 },
  { age_yr: 0.6899, L: 0.8255, M: 8.876,   S: 0.1101 },
  { age_yr: 0.7666, L: 0.7673, M: 9.2014,  S: 0.1095 },
  { age_yr: 0.8433, L: 0.7129, M: 9.5065,  S: 0.1093 },
  { age_yr: 0.9199, L: 0.6623, M: 9.7936,  S: 0.1087 },
  { age_yr: 0.9966, L: 0.615,  M: 10.0659, S: 0.1087 },
  { age_yr: 1.0732, L: 0.5709, M: 10.3252, S: 0.1084 },
  { age_yr: 1.1499, L: 0.5295, M: 10.5737, S: 0.1079 },
  { age_yr: 1.2266, L: 0.4908, M: 10.8122, S: 0.1081 },
  { age_yr: 1.5,    L: 0.3698, M: 11.6017, S: 0.1079 },
];

const NL5_WEIGHT_GIRLS: LMSRow[] = [
  { age_yr: 0.0192, L: 1.1962, M: 3.6628,  S: 0.1154 },
  { age_yr: 0.0383, L: 1.1741, M: 3.8342,  S: 0.1154 },
  { age_yr: 0.0575, L: 1.1519, M: 4.0065,  S: 0.1154 },
  { age_yr: 0.0767, L: 1.1297, M: 4.1789,  S: 0.1153 },
  { age_yr: 0.0958, L: 1.1076, M: 4.3503,  S: 0.1153 },
  { age_yr: 0.115,  L: 1.0854, M: 4.5224,  S: 0.1153 },
  { age_yr: 0.1342, L: 1.0632, M: 4.6936,  S: 0.1155 },
  { age_yr: 0.1533, L: 1.0412, M: 4.8625,  S: 0.1152 },
  { age_yr: 0.1725, L: 1.0191, M: 5.0303,  S: 0.1152 },
  { age_yr: 0.1916, L: 0.9972, M: 5.1949,  S: 0.1153 },
  { age_yr: 0.23,   L: 0.9535, M: 5.5179,  S: 0.1153 },
  { age_yr: 0.2683, L: 0.9104, M: 5.8277,  S: 0.1154 },
  { age_yr: 0.3066, L: 0.8679, M: 6.1234,  S: 0.1151 },
  { age_yr: 0.345,  L: 0.826,  M: 6.404,   S: 0.1153 },
  { age_yr: 0.3833, L: 0.7851, M: 6.6676,  S: 0.1153 },
  { age_yr: 0.4216, L: 0.7452, M: 6.9162,  S: 0.1155 },
  { age_yr: 0.46,   L: 0.7062, M: 7.1525,  S: 0.1152 },
  { age_yr: 0.4983, L: 0.6684, M: 7.376,   S: 0.1151 },
  { age_yr: 0.5366, L: 0.6317, M: 7.5878,  S: 0.1153 },
  { age_yr: 0.6133, L: 0.5617, M: 7.9784,  S: 0.1157 },
  { age_yr: 0.6899, L: 0.4965, M: 8.3264,  S: 0.1157 },
  { age_yr: 0.7666, L: 0.4358, M: 8.6387,  S: 0.1158 },
  { age_yr: 0.8433, L: 0.3795, M: 8.9207,  S: 0.116  },
  { age_yr: 0.9199, L: 0.3274, M: 9.1799,  S: 0.1157 },
  { age_yr: 0.9966, L: 0.2789, M: 9.4245,  S: 0.1159 },
  { age_yr: 1.0732, L: 0.2338, M: 9.6592,  S: 0.1159 },
  { age_yr: 1.1499, L: 0.1917, M: 9.8866,  S: 0.1167 },
  { age_yr: 1.2266, L: 0.1522, M: 10.1078, S: 0.1162 },
  { age_yr: 1.5,    L: 0.0286, M: 10.8514, S: 0.1172 },
];

// ─── NL1997 head circumference (Fredriks 2000) ─────────────────────
// The Fifth Dutch Growth Study (2009) did NOT update HC for the general
// Dutch population, so growthscreener — and we — use NL1997 hdc when "NL5"
// is selected. Pediatric Research 47, 316-323. Source: nlreferences pkg.
const NL_HDC_BOYS: LMSRow[] = [
  { age_yr: 0,      L: 1, M: 35.67,  S: 0.0375 },
  { age_yr: 0.0192, L: 1, M: 36.1,   S: 0.0373 },
  { age_yr: 0.0383, L: 1, M: 36.505, S: 0.0371 },
  { age_yr: 0.0575, L: 1, M: 36.981, S: 0.0368 },
  { age_yr: 0.0767, L: 1, M: 37.451, S: 0.0365 },
  { age_yr: 0.0958, L: 1, M: 37.909, S: 0.0361 },
  { age_yr: 0.115,  L: 1, M: 38.351, S: 0.0357 },
  { age_yr: 0.1342, L: 1, M: 38.774, S: 0.0353 },
  { age_yr: 0.1533, L: 1, M: 39.179, S: 0.035  },
  { age_yr: 0.1725, L: 1, M: 39.566, S: 0.0346 },
  { age_yr: 0.1916, L: 1, M: 39.933, S: 0.0343 },
  { age_yr: 0.23,   L: 1, M: 40.611, S: 0.0335 },
  { age_yr: 0.2683, L: 1, M: 41.231, S: 0.0328 },
  { age_yr: 0.3066, L: 1, M: 41.813, S: 0.0323 },
  { age_yr: 0.345,  L: 1, M: 42.354, S: 0.0316 },
  { age_yr: 0.3833, L: 1, M: 42.853, S: 0.0313 },
  { age_yr: 0.4216, L: 1, M: 43.312, S: 0.0309 },
  { age_yr: 0.46,   L: 1, M: 43.732, S: 0.0306 },
  { age_yr: 0.4983, L: 1, M: 44.118, S: 0.0306 },
  { age_yr: 0.5366, L: 1, M: 44.471, S: 0.0304 },
  { age_yr: 0.6133, L: 1, M: 45.091, S: 0.0304 },
  { age_yr: 0.6899, L: 1, M: 45.625, S: 0.0307 },
  { age_yr: 0.7666, L: 1, M: 46.087, S: 0.0308 },
  { age_yr: 0.8433, L: 1, M: 46.491, S: 0.031  },
  { age_yr: 0.9199, L: 1, M: 46.851, S: 0.0314 },
  { age_yr: 0.9966, L: 1, M: 47.17,  S: 0.0316 },
  { age_yr: 1.0732, L: 1, M: 47.444, S: 0.0318 },
  { age_yr: 1.1499, L: 1, M: 47.679, S: 0.0321 },
  { age_yr: 1.2266, L: 1, M: 47.883, S: 0.0324 },
  { age_yr: 1.5,    L: 1, M: 48.454, S: 0.0328 },
];

const NL_HDC_GIRLS: LMSRow[] = [
  { age_yr: 0,      L: 1, M: 35.04,  S: 0.0359 },
  { age_yr: 0.0192, L: 1, M: 35.43,  S: 0.0358 },
  { age_yr: 0.0383, L: 1, M: 35.81,  S: 0.0357 },
  { age_yr: 0.0575, L: 1, M: 36.152, S: 0.0355 },
  { age_yr: 0.0767, L: 1, M: 36.551, S: 0.0352 },
  { age_yr: 0.0958, L: 1, M: 36.948, S: 0.0349 },
  { age_yr: 0.115,  L: 1, M: 37.342, S: 0.0345 },
  { age_yr: 0.1342, L: 1, M: 37.73,  S: 0.0344 },
  { age_yr: 0.1533, L: 1, M: 38.109, S: 0.0341 },
  { age_yr: 0.1725, L: 1, M: 38.477, S: 0.0337 },
  { age_yr: 0.1916, L: 1, M: 38.833, S: 0.0337 },
  { age_yr: 0.23,   L: 1, M: 39.5,   S: 0.0331 },
  { age_yr: 0.2683, L: 1, M: 40.117, S: 0.0329 },
  { age_yr: 0.3066, L: 1, M: 40.685, S: 0.0322 },
  { age_yr: 0.345,  L: 1, M: 41.206, S: 0.032  },
  { age_yr: 0.3833, L: 1, M: 41.682, S: 0.0317 },
  { age_yr: 0.4216, L: 1, M: 42.117, S: 0.0313 },
  { age_yr: 0.46,   L: 1, M: 42.512, S: 0.0313 },
  { age_yr: 0.4983, L: 1, M: 42.871, S: 0.031  },
  { age_yr: 0.5366, L: 1, M: 43.199, S: 0.0305 },
  { age_yr: 0.6133, L: 1, M: 43.781, S: 0.0306 },
  { age_yr: 0.6899, L: 1, M: 44.29,  S: 0.0305 },
  { age_yr: 0.7666, L: 1, M: 44.744, S: 0.0306 },
  { age_yr: 0.8433, L: 1, M: 45.155, S: 0.0306 },
  { age_yr: 0.9199, L: 1, M: 45.522, S: 0.0305 },
  { age_yr: 0.9966, L: 1, M: 45.852, S: 0.0308 },
  { age_yr: 1.0732, L: 1, M: 46.147, S: 0.031  },
  { age_yr: 1.1499, L: 1, M: 46.411, S: 0.031  },
  { age_yr: 1.2266, L: 1, M: 46.647, S: 0.0311 },
  { age_yr: 1.5,    L: 1, M: 47.291, S: 0.0313 },
];

// ─── NL3 (Third Dutch Growth Study, 1980) — weight only ──────────────
// NL1980 / Roede & van Wieringen. nlreferences ships wgt + bmi + wfh for
// this cohort; no length-for-age, no head circumference (those references
// only arrived in NL4=1997). Source: nlreferences pkg, trimmed to ≤1.5y.
const NL3_WEIGHT_BOYS: LMSRow[] = [
  { age_yr: 0.0192, L: 0.319, M: 3.53,  S: 0.1099 },
  { age_yr: 0.0383, L: 0.317, M: 3.73,  S: 0.1096 },
  { age_yr: 0.0575, L: 0.314, M: 3.92,  S: 0.1094 },
  { age_yr: 0.0767, L: 0.312, M: 4.12,  S: 0.1092 },
  { age_yr: 0.0958, L: 0.309, M: 4.31,  S: 0.109  },
  { age_yr: 0.115,  L: 0.307, M: 4.51,  S: 0.1088 },
  { age_yr: 0.1342, L: 0.304, M: 4.7,   S: 0.1085 },
  { age_yr: 0.1533, L: 0.302, M: 4.89,  S: 0.1083 },
  { age_yr: 0.1725, L: 0.299, M: 5.07,  S: 0.1081 },
  { age_yr: 0.1916, L: 0.297, M: 5.26,  S: 0.1079 },
  { age_yr: 0.23,   L: 0.293, M: 5.61,  S: 0.1075 },
  { age_yr: 0.2491, L: 0.29,  M: 5.78,  S: 0.1073 },
  { age_yr: 0.2917, L: 0.285, M: 6.15,  S: 0.1069 },
  { age_yr: 0.3333, L: 0.281, M: 6.48,  S: 0.1066 },
  { age_yr: 0.375,  L: 0.277, M: 6.8,   S: 0.1063 },
  { age_yr: 0.4167, L: 0.272, M: 7.09,  S: 0.106  },
  { age_yr: 0.4583, L: 0.268, M: 7.37,  S: 0.1057 },
  { age_yr: 0.5,    L: 0.265, M: 7.63,  S: 0.1055 },
  { age_yr: 0.5833, L: 0.257, M: 8.11,  S: 0.1051 },
  { age_yr: 0.6667, L: 0.25,  M: 8.56,  S: 0.1048 },
  { age_yr: 0.75,   L: 0.242, M: 8.97,  S: 0.1045 },
  { age_yr: 0.8333, L: 0.235, M: 9.36,  S: 0.1043 },
  { age_yr: 0.9167, L: 0.227, M: 9.72,  S: 0.1042 },
  { age_yr: 1,      L: 0.22,  M: 10.06, S: 0.1041 },
  { age_yr: 1.0833, L: 0.212, M: 10.39, S: 0.104  },
  { age_yr: 1.1667, L: 0.204, M: 10.69, S: 0.104  },
  { age_yr: 1.25,   L: 0.197, M: 10.99, S: 0.104  },
  { age_yr: 1.3333, L: 0.189, M: 11.26, S: 0.104  },
  { age_yr: 1.4167, L: 0.182, M: 11.52, S: 0.104  },
  { age_yr: 1.5,    L: 0.174, M: 11.77, S: 0.1041 },
];

const NL3_WEIGHT_GIRLS: LMSRow[] = [
  { age_yr: 0.0219, L: 0.526, M: 3.42,  S: 0.1044 },
  { age_yr: 0.0383, L: 0.517, M: 3.57,  S: 0.1043 },
  { age_yr: 0.0575, L: 0.505, M: 3.75,  S: 0.1043 },
  { age_yr: 0.0767, L: 0.494, M: 3.93,  S: 0.1042 },
  { age_yr: 0.0958, L: 0.483, M: 4.11,  S: 0.1041 },
  { age_yr: 0.115,  L: 0.472, M: 4.29,  S: 0.1041 },
  { age_yr: 0.1342, L: 0.461, M: 4.46,  S: 0.104  },
  { age_yr: 0.1533, L: 0.45,  M: 4.64,  S: 0.104  },
  { age_yr: 0.1725, L: 0.439, M: 4.81,  S: 0.1039 },
  { age_yr: 0.1916, L: 0.429, M: 4.98,  S: 0.1039 },
  { age_yr: 0.23,   L: 0.407, M: 5.31,  S: 0.1038 },
  { age_yr: 0.2491, L: 0.397, M: 5.47,  S: 0.1038 },
  { age_yr: 0.2917, L: 0.375, M: 5.81,  S: 0.1037 },
  { age_yr: 0.3333, L: 0.354, M: 6.12,  S: 0.1036 },
  { age_yr: 0.375,  L: 0.334, M: 6.42,  S: 0.1036 },
  { age_yr: 0.4167, L: 0.315, M: 6.69,  S: 0.1035 },
  { age_yr: 0.4583, L: 0.297, M: 6.95,  S: 0.1035 },
  { age_yr: 0.5,    L: 0.279, M: 7.2,   S: 0.1035 },
  { age_yr: 0.5833, L: 0.247, M: 7.66,  S: 0.1035 },
  { age_yr: 0.6667, L: 0.218, M: 8.07,  S: 0.1036 },
  { age_yr: 0.75,   L: 0.19,  M: 8.46,  S: 0.1037 },
  { age_yr: 0.8333, L: 0.165, M: 8.81,  S: 0.1039 },
  { age_yr: 0.9167, L: 0.14,  M: 9.15,  S: 0.1041 },
  { age_yr: 1,      L: 0.117, M: 9.47,  S: 0.1043 },
  { age_yr: 1.0833, L: 0.094, M: 9.78,  S: 0.1045 },
  { age_yr: 1.1667, L: 0.073, M: 10.08, S: 0.1047 },
  { age_yr: 1.25,   L: 0.052, M: 10.37, S: 0.105  },
  { age_yr: 1.3333, L: 0.031, M: 10.64, S: 0.1053 },
  { age_yr: 1.4167, L: 0.012, M: 10.91, S: 0.1056 },
  { age_yr: 1.5,    L: -0.006, M: 11.15, S: 0.1058 },
];

type TableKey = `${ReferenceId}.${Metric}.${Sex}`;
const TABLES: Partial<Record<TableKey, LMSRow[]>> = {
  'who.weight.M': WHO_WEIGHT_BOYS,
  'who.weight.F': WHO_WEIGHT_GIRLS,
  'who.length.M': WHO_LENGTH_BOYS,
  'who.length.F': WHO_LENGTH_GIRLS,
  'nl3.weight.M': NL3_WEIGHT_BOYS,
  'nl3.weight.F': NL3_WEIGHT_GIRLS,
  'nl4.weight.M': NL4_WEIGHT_BOYS,
  'nl4.weight.F': NL4_WEIGHT_GIRLS,
  'nl4.length.M': NL4_LENGTH_BOYS,
  'nl4.length.F': NL4_LENGTH_GIRLS,
  'nl4.head_circumference.M': NL_HDC_BOYS,
  'nl4.head_circumference.F': NL_HDC_GIRLS,
  'nl5.weight.M': NL5_WEIGHT_BOYS,
  'nl5.weight.F': NL5_WEIGHT_GIRLS,
  'nl5.length.M': NL5_LENGTH_BOYS,
  'nl5.length.F': NL5_LENGTH_GIRLS,
};

/** Pick the weight reference for a baby, mirroring R's pick_reference_wgt:
 *  preterm (ga_days < 259 = <37w completed) routes to the NL2012 preterm
 *  LMS for the closest tabulated ga (clamped to 25..36 weeks). Term babies
 *  use the requested reference (NL5 default). Returns null when the
 *  combination is unsupported. */
export function pickWeightReference(
  ref: ReferenceId,
  sex: Sex,
  gaDays: number | null | undefined,
): LMSRow[] | null {
  if (gaDays != null) {
    const gaW = Math.floor(gaDays / 7);
    if (gaW < 37) {
      const clamped = Math.max(25, Math.min(36, gaW));
      return PRETERM_WGT[sex][clamped] ?? null;
    }
  }
  return getReferenceTable(ref, 'weight', sex);
}

/** Looks up an LMS table, applying best-practice fallbacks per metric:
 *  - HC: every NL reference falls back to NL1997 (NL5 didn't ship HC).
 *  - NL3 (1980): only has weight; length falls back to NL4 (1997).
 *  - NL5 (2009): no HC update from NL4; length is its own; weight is its own.
 *  Unsupported combinations return null and the caller hides curves. */
export function getReferenceTable(ref: ReferenceId, metric: Metric, sex: Sex): LMSRow[] | null {
  const direct = TABLES[`${ref}.${metric}.${sex}`];
  if (direct) return direct;
  // HC: every NL reference inherits from NL4 (which holds the NL1997 table).
  if (metric === 'head_circumference' && (ref === 'nl3' || ref === 'nl5')) {
    return TABLES[`nl4.head_circumference.${sex}`] ?? null;
  }
  // NL3 length: not published; use NL4 (1997).
  if (ref === 'nl3' && metric === 'length') {
    return TABLES[`nl4.length.${sex}`] ?? null;
  }
  // NL5 inherits any other missing table from NL4.
  if (ref === 'nl5') return TABLES[`nl4.${metric}.${sex}`] ?? null;
  return null;
}

export const REFERENCES: { id: ReferenceId; label: string; note: string }[] = [
  {
    id: 'who',
    label: 'WHO 2006',
    note: 'WHO Child Growth Standards — international healthy-breastfed reference',
  },
  {
    id: 'nl5',
    label: 'Dutch (NL5)',
    note: 'Fifth Dutch Growth Study, 2009 (Schönbeck) — current Dutch standard',
  },
  {
    id: 'nl4',
    label: 'Dutch (NL4)',
    note: 'Fourth Dutch Growth Study, 1997 (Fredriks)',
  },
  {
    id: 'nl3',
    label: 'Dutch (NL3)',
    note: 'Third Dutch Growth Study, 1980 — weight only; length/HC fall back to NL4',
  },
];
